/**
 * `clientShortName` — a compact client label for the discreet client rows in
 * the case navigator + the workspace-pane title (operator 2026-07-06: the
 * client is critical context but must stay subordinate to the case, so it's
 * shown small/thin/light and SHORT).
 *
 * Clients carry no dedicated shortname field yet — a curated `shortName` /
 * `alias` on the client model is a possible backend follow-up. Until then we
 * derive one from the registry display name: strip a trailing parenthetical /
 * bracket annotation (e.g. the dev fixtures' "(dev/E2E)" suffix) + collapse
 * whitespace; the UI truncates for width. Never returns an empty string when a
 * usable name (or fallback) is available, so a client row never goes blank.
 */
export function clientShortName(
  name: string | undefined | null,
  fallback = "",
): string {
  const raw = (name ?? "").replace(/\s+/g, " ").trim();
  if (!raw) return fallback;
  // Drop a trailing "(...)" / "[...]" annotation, but only if something remains.
  const stripped = raw.replace(/\s*[([][^)\]]*[)\]]\s*$/, "").trim();
  return stripped || raw;
}

/**
 * The short client label for the case navigator + the case-pane title bar.
 * Prefers the curated stored `@alias` handle (operator 2026-07-06) — shown WITH a
 * leading `@` because it IS a reference handle (`@ClientHandle`) — and falls back
 * to a shortname derived from the full `name` (no `@`) when no alias is set yet.
 * `fallback` covers the not-yet-loaded client (e.g. the clientId).
 */
export function clientDisplayLabel(
  client: { alias?: string | null; name?: string | null } | undefined | null,
  fallback = "",
): string {
  const alias = (client?.alias ?? "").replace(/^@/, "").trim();
  if (alias) return `@${alias}`;
  return clientShortName(client?.name, fallback);
}
